import { ComponentProps, PropsWithChildren, ReactNode } from 'react';

import { Menu } from '~/modules/elements/akui/menu';
import { useBackground } from '~/modules/elements/background-context';
import Logo from '~/modules/elements/logo';
import RecommendedBrowsers from '~/modules/elements/recommended-browsers';
import LayoutGame from '~/routes/layout-game';
import GithubRibbon from '~/routes/welcome/github-ribbon';

/**
 * The column every page in this family caps itself at - 1600 of the frame's 1920, and the same
 * 83.3% of the width on any 16:9 display above it, so the toolbar row starts at the same x on all
 * of them. `MenuContainer` pins its own max-width at three breakpoints, so overriding it means
 * overriding every one of them.
 */
export const PAGE_CARD = 'sm:max-w-[min(94vw,100rem)] lg:max-w-[min(94vw,100rem)] 2xl:max-w-[min(94vw,100rem)]';

type Props = PropsWithChildren<{
  supportedBrowsers?: boolean;
  /**
   * Rendered to the right of the menu on desktop only. Narrow screens have no room for a second
   * column, so the caller places its own copy inside the menu instead.
   */
  sidePanel?: ReactNode;
  /**
   * Replaces the plain wordmark above the menu. Only the main menu uses this - every other screen
   * is somewhere you arrived from there, and a centrepiece on each of them would say nothing.
   */
  logo?: ReactNode;
  /**
   * Centre the wordmark and menu in the viewport instead of hanging them from the top. Opt-in
   * because every other screen here is a page you read from the top down.
   *
   * Centred with auto margins rather than `justify-center`: on a screen too short for the content,
   * centring overflows in both directions and the top of the menu can neither be seen nor scrolled
   * to, while an auto margin simply collapses to nothing and the page falls back to scrolling.
   */
  centered?: boolean;
  /**
   * Rendered at the very bottom of the viewport-height box `centered` makes. Anything the caller
   * puts after `MenuWithLogo` instead would be pushed past that box and make the page scroll.
   */
  footer?: ReactNode;
}> &
  ComponentProps<typeof Menu>;

function MenuWithLogo({ children, supportedBrowsers, sidePanel, logo, centered = false, footer, ...props }: Props) {
  useBackground(true);

  const column = (
    <div className={`mobile:gap-4 flex w-screen flex-col items-center gap-8 ${centered ? 'my-auto pt-8' : 'pt-8'}`}>
      {logo ?? (
        <div className="mobile:text-5xl text-6xl">
          <Logo />
        </div>
      )}
      {supportedBrowsers && <RecommendedBrowsers />}
      {sidePanel ? (
        // `1fr auto 1fr`, nothing in the first column: the two `1fr` tracks always split the
        // leftover space evenly, so the middle (menu) column stays centered on the page no matter
        // how wide the side panel is, instead of the pair centering as a unit.
        <div className="grid w-full grid-cols-[1fr_auto_1fr] items-start gap-6">
          <div />
          <Menu {...props}>{children}</Menu>
          <div className="hidden w-[26rem] shrink-0 lg:block">{sidePanel}</div>
        </div>
      ) : (
        <Menu {...props}>{children}</Menu>
      )}
    </div>
  );

  return (
    <LayoutGame>
      <GithubRibbon />
      {centered ? (
        // Exactly one viewport tall, with the hint strip's own band left clear at the foot of it -
        // the strip is fixed to the bottom edge and would otherwise lie across the footer.
        <div className="flex min-h-dvh w-screen flex-col pb-14">
          {column}
          {footer}
        </div>
      ) : (
        <>
          {column}
          {footer}
        </>
      )}
    </LayoutGame>
  );
}

export default MenuWithLogo;
